/**
 * @file     index.ts
 * @readme   ./CLAUDE.md
 * @purpose  Initializes i18next with the core English bundle (workspace namespaces register via ./deferred), detects the browser language on startup, deferred-loads non-English bundles, and applies RTL/LTR direction to the HTML document.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { defaultLanguage, rtlLanguages, supportedLanguages, type AppLanguage } from './languages';
import { loadLocaleBundle } from './loadLocaleBundle';
import { enCore } from './resources/en/core';

const loadedTranslationBundles = new Set<AppLanguage>([defaultLanguage]);

const normalizeLanguageCode = (rawCode: string | undefined): AppLanguage => {
  if (!rawCode) {
    return defaultLanguage;
  }

  const candidate = rawCode.toLowerCase().split('-')[0] as AppLanguage;

  if (supportedLanguages.includes(candidate)) {
    return candidate;
  }

  return defaultLanguage;
};

export const detectLanguage = (): AppLanguage => {
  if (typeof window === 'undefined') return defaultLanguage;

  // 1. Persisted user preference wins
  const saved = localStorage.getItem('relta-lang');
  if (saved && supportedLanguages.includes(saved.split('-')[0] as AppLanguage)) {
    return normalizeLanguageCode(saved);
  }

  // 2. Walk full browser language priority list
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const lang of candidates) {
    const normalized = normalizeLanguageCode(lang);
    if (supportedLanguages.includes(normalized)) return normalized;
  }

  return defaultLanguage;
};

const applyWebDirection = (language: string) => {
  if (typeof document === 'undefined') return;
  const normalized = normalizeLanguageCode(language);
  document.documentElement.lang = normalized;
  document.documentElement.dir = rtlLanguages.includes(normalized) ? 'rtl' : 'ltr';
};

export const ensureLanguageBundle = async (language: string) => {
  const normalized = normalizeLanguageCode(language);

  if (loadedTranslationBundles.has(normalized)) {
    return normalized;
  }

  const bundle = await loadLocaleBundle(normalized);

  i18n.addResourceBundle(normalized, 'translation', bundle.translation, true, true);

  if (bundle.pageContent) {
    i18n.addResourceBundle(normalized, 'translation', bundle.pageContent, true, true);
  }

  loadedTranslationBundles.add(normalized);

  return normalized;
};

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: {
      [defaultLanguage]: {
        translation: enCore
      }
    },
    lng: defaultLanguage,
    fallbackLng: defaultLanguage,
    interpolation: {
      escapeValue: false
    },
    initImmediate: false,
    react: {
      useSuspense: false,
    },
    supportedLngs: supportedLanguages,
    returnNull: false,
  });

  i18n.on('languageChanged', (language) => {
    applyWebDirection(language);
  });
}

export { i18n };
export * from './languages';
export * from './useAppLanguage';
